import { Dialog, DialogContent } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import PreviewImageDialog from "@entrilia/shared/components/previewFile/PreviewImageDialog";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { CommunicationImageSource } from "../investorCommunicationDetailsTypes";

interface Props {
  source: CommunicationImageSource;
  onClose: () => void;
}

// Matches the minimum size of PreviewImageDialog so the switch to the loaded image does not resize the dialog.
const PlaceholderDialog = ({ onClose, children }: PropsWithChildren<{ onClose: () => void }>) => (
  <Dialog open onClose={onClose} slotProps={{ paper: { sx: { minWidth: "20rem", minHeight: "20rem" } } }}>
    <DialogContent sx={{ display: "flex" }}>{children}</DialogContent>
  </Dialog>
);

const SavedHeroImageDialog = ({ communicationId, onClose }: { communicationId: string; onClose: () => void }) => {
  const getDownloadInfo = useCallback(
    () => adminApi.getInvestorCommunicationHeroImageDownloadInfo(communicationId),
    [communicationId]
  );

  const [downloadInfo, fetchError] = useFetch(getDownloadInfo);

  if (fetchError) {
    logError(fetchError, "[InvestorCommunicationHeroImageDialog] getInvestorCommunicationHeroImageDownloadInfo");
    return (
      <PlaceholderDialog onClose={onClose}>
        <DataLoadingFailed title="Failed to load the image" />
      </PlaceholderDialog>
    );
  }

  if (downloadInfo === undefined) {
    return (
      <PlaceholderDialog onClose={onClose}>
        <InlineLoader />
      </PlaceholderDialog>
    );
  }

  return <PreviewImageDialog src={downloadInfo.downloadUrl} title={downloadInfo.fileName} onClose={onClose} />;
};

const InvestorCommunicationHeroImageDialog = ({ source, onClose }: Props) => {
  switch (source.kind) {
    case "saved":
      return <SavedHeroImageDialog communicationId={source.communicationId} onClose={onClose} />;
    case "local":
      return <PreviewImageDialog src={source.url} title={source.fileName} onClose={onClose} />;
    case "url":
      return <PreviewImageDialog src={source.url} title="Thumbnail" onClose={onClose} />;
  }
};

export default InvestorCommunicationHeroImageDialog;
